/**
 * The configuration item form in a modal.
 *
 * Thin, because the form itself is `LabConfigurationItemForm` - the builder opens the same thing in a
 * slide-over so the tree stays visible, and two copies of a form with five JSON specs and a
 * prerequisite graph in it is two places for the rules to drift. What is left here is what belongs to
 * *this* way of opening it: the draft, the key clash check, the save, and the errors.
 */

import React, { useEffect, useMemo, useState } from 'react';
import StbModal from '../security-toolbox-ui/StbModal';
import {
  useLabConfigurationItems,
  useLabDeviceTypes,
  useLabExecutionMethods,
  useLabProducts,
  useSaveLabRow,
} from '../../hooks/lab-hooks';
import {
  LabConfigurationItem,
  labItemBlockers,
  labSlugify,
  LAB_TABLES,
} from '../../support/labService';
import { labErrorMessage } from './LabShared';
import LabConfigurationItemForm, {
  LabItemAttachContext,
  labItemFormBlockers,
  LAB_ITEM_EMPTY,
  LAB_ITEM_SPEC_FIELDS,
} from './LabConfigurationItemForm';

export type LabConfigurationItemEditorModalProps = {
  open: boolean;
  /** Null creates. An existing row keeps its key, because generated steps are named from it. */
  item: LabConfigurationItem | null;
  /**
   * The device type a new item starts out written for.
   *
   * Set when this is opened from a device on the lab map: the author is on a Windows host, so a new
   * item is for Windows hosts until they say otherwise. Ignored when editing - an existing item's
   * type is its own, and having it change because of where the form was opened from would be a trap.
   */
  defaultDeviceType?: number | null;
  /**
   * Set when the form was opened from inside a scenario, so it can ask which device the attachment
   * lands on. Absent from the library, where there is no scenario to answer for.
   */
  attach?: LabItemAttachContext | null;
  onClose: () => void;
  onSaved?: (item: LabConfigurationItem) => void;
};

const LabConfigurationItemEditorModal: React.FC<LabConfigurationItemEditorModalProps> = ({
  open,
  item,
  defaultDeviceType = null,
  attach = null,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<Partial<LabConfigurationItem>>(LAB_ITEM_EMPTY);
  const [jsonErrors, setJsonErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const { data: items } = useLabConfigurationItems({}, { enabled: open });
  const { data: deviceTypes } = useLabDeviceTypes({}, { enabled: open });
  const { data: methods } = useLabExecutionMethods({}, { enabled: open });

  const save = useSaveLabRow<Partial<LabConfigurationItem> & { id?: number }>(LAB_TABLES.configurationItem);

  useEffect(() => {
    if (!open) return;

    setDraft(
      item ? { ...item } : { ...LAB_ITEM_EMPTY, device_type: defaultDeviceType ?? null },
    );
    setJsonErrors({});
    setFormError(null);
    save.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id, defaultDeviceType]);

  const catalogue = useMemo(() => items || [], [items]);

  const chosenType = useMemo(
    () => (deviceTypes || []).find((candidate) => candidate.id === draft.device_type) || null,
    [deviceTypes, draft.device_type],
  );

  // Only once a family type has been chosen. Two thousand products is not a fetch to make on the
  // chance that this item turns out to be written for one of them.
  const { data: products, isLoading: productsLoading } = useLabProducts({
    enabled: open && !!chosenType?.requires_product,
  });

  const set = (patch: Partial<LabConfigurationItem>) => {
    setFormError(null);
    setDraft((current) => ({ ...current, ...patch }));
  };

  /** Derived from the name for a new item; frozen once saved. Duplicated by the form for display. */
  const generatedKey = useMemo(
    () => (item ? item.key : labSlugify(draft.name || '')),
    [item, draft.name],
  );

  /**
   * What the row will be flagged for once saved, as opposed to what stops it saving.
   *
   * Run against the draft rather than the saved row so the warning appears while there is still a
   * form open to fix it in.
   */
  const rowBlockers = useMemo(
    () =>
      labItemBlockers({
        ...(LAB_ITEM_EMPTY as LabConfigurationItem),
        ...draft,
      } as LabConfigurationItem),
    [draft],
  );

  const saveBlockers = useMemo(() => labItemFormBlockers(draft, attach), [draft, attach]);

  const handleSubmit = async () => {
    if (saveBlockers.length) {
      setFormError(saveBlockers[0]);
      return;
    }

    if (!generatedKey) {
      setFormError('No key could be derived from that name. Use letters or digits somewhere in it.');
      return;
    }

    const clash = catalogue.find(
      (candidate) => candidate.id !== draft.id && candidate.key === generatedKey,
    );

    if (clash) {
      setFormError(
        `"${clash.name}" already uses the key ${generatedKey}. Edit that item instead, or name this one differently - the key is what generated steps are named from, so two items cannot share one.`,
      );
      return;
    }

    const badSpec = LAB_ITEM_SPEC_FIELDS.find((spec) => jsonErrors[spec.field]);

    if (badSpec) {
      setFormError(`${badSpec.schema.title}: ${jsonErrors[badSpec.field]}`);
      return;
    }

    if (jsonErrors.metadata) {
      setFormError(`Metadata: ${jsonErrors.metadata}`);
      return;
    }

    setFormError(null);

    try {
      const saved = await save.mutateAsync({ ...draft, key: generatedKey });
      onSaved?.(saved as LabConfigurationItem);
      onClose();
    } catch {
      // Surfaced through save.error.
    }
  };

  const submitError = formError || (save.isError ? labErrorMessage(save.error) : null);

  return (
    <StbModal
      open={open}
      role="update"
      title={item ? `Edit ${item.name}` : 'New configuration item'}
      size="large"
      scrollable
      submitLabel={item ? 'Save item' : 'Create item'}
      // Disabled on the three things without which the item does nothing, and on a spec that does
      // not parse. Every softer problem is a warning on the form, because an author part-way through
      // swapping a device type has to be able to put it down.
      submitDisabled={
        !!saveBlockers.length ||
        save.isPending ||
        Object.values(jsonErrors).some((error) => !!error)
      }
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        submitError ? (
          <p className="m-0 text-sm text-amber-800">{submitError}</p>
        ) : saveBlockers.length ? (
          <ul className="m-0 list-disc pl-5 text-sm text-gray-600">
            {saveBlockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        ) : undefined
      }
    >
      <LabConfigurationItemForm
        item={item}
        draft={draft}
        onChange={set}
        catalogue={catalogue}
        deviceTypes={deviceTypes || []}
        methods={methods || []}
        products={products || []}
        productsLoading={productsLoading}
        jsonErrors={jsonErrors}
        onJsonError={(field, error) =>
          setJsonErrors((current) => ({ ...current, [field]: error }))
        }
        attach={attach}
        rowBlockers={rowBlockers}
      />
    </StbModal>
  );
};

export default LabConfigurationItemEditorModal;
